import { addBeautifiedPrefixToId } from './beautifiedPrefix';
import { SPINNER_CLASSNAME } from './constants';
import getSharingInformation from '../actions/getSharingInformation';
import { onSharePointLinkDetected } from '../actions/internalActions';
import LinkPlugin from '../plugin/LinkPlugin';
import { logUsage } from 'owa-analytics';
import { AttachmentWellViewState } from 'owa-attachment-well-data';
import { lazyDocLinkHadPreviewInfo } from 'owa-doc-link-click-handler';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyLinkBootStrap, getSharingLink, SharingLinkInfo } from 'owa-link-data';
import { lazyTryCheckPermForOneLink } from 'owa-recipient-permission-checker';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetSharingInfoResponse from 'owa-service/lib/contract/GetSharingInfoResponse';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { trace } from 'owa-trace';
import { PositionType } from 'roosterjs-editor-types';
import {
    addComposeLinkViewState,
    createSharingLinkView,
    displaySpinnerOverlay,
} from 'owa-compose-link';
import {
    COMPOSE_ONLY_ATTACHMENT_CONTENT_END,
    COMPOSE_ONLY_ATTACHMENT_CONTENT_START,
} from 'owa-attachment-link-common';

export default async function getSharingInfoToTryUpdateLink(
    linkPlugin: LinkPlugin,
    shouldBeautifyLink: boolean, // We would display a spinner during server call, change the text, add file icon, display error icon & red text for links with sharing issues, context menu on click
    shouldRehydrate: boolean, // We won't change the link, but display error icon & red text for links with sharing issues, context menu on click
    isPreviouslyBeautified: boolean,
    anchorElement: HTMLAnchorElement,
    viewState: AttachmentWellViewState,
    linkIndex: number, // The link index when there are a collection of anchor elements; otherwise, just put 0
    linkIdsWithSpinner: string[],
    shouldTryShowContextMenuOnMount: boolean = false
): Promise<void> {
    // Todo: VSTO 39277: refactor getSharingInfoToTryUpdateLink and its parameters
    trace.info('LinkPlugin.getSharingInfoToTryUpdateLink');
    let addedElements: Node[] | null;
    if (shouldBeautifyLink) {
        // Spinner is only needed when we will beautify the link
        linkPlugin.editor.addUndoSnapshot();
        linkIdsWithSpinner.push(anchorElement.id);
        addedElements = displaySpinner(anchorElement);
        linkPlugin.editor.select(anchorElement, PositionType.After);
        linkPlugin.editor.triggerContentChangedEvent('Link');
        logUsage('AttachmentComposeSPLinksShouldReplaceText');
    }

    const linkId = anchorElement.id;

    // We use the lazy version of GetSharingLink to make sure the package loads
    await lazyLinkBootStrap.import(); // This is to load the owa-link-data package
    let sharingLink: SharingLinkInfo = getSharingLink(anchorElement.id);
    if (sharingLink && anchorElement.href !== sharingLink.url) {
        sharingLink = null;
        trace.error('Stored url is different from the href on anchor element');
    }

    let response: GetSharingInfoResponse;

    // After user leaves the compose and comes back, the plugin has been disposed and we need to rerender the UI
    // But we don't need to perform the following tasks
    // 1. send the server call for links we already have the getSharingInformation response in the model store(sharingLink will not be null)
    // 2. we never pass true for shouldBeautifyLink, so we don't need to remove Spinner nor add prefix to id.
    if (!sharingLink) {
        logUsage('AttachmentComposeSPLinks');
        response = await getSharingInformation(anchorElement.href);
        if (shouldBeautifyLink) {
            const index = linkIdsWithSpinner.indexOf(anchorElement.id);
            linkIdsWithSpinner.splice(index, 1);
            removeSpinner(anchorElement, addedElements);
        }

        // The following check is only needed after async call.
        if (!linkPlugin.editor) {
            // This plugin has been disposed. No need to do anything
            logUsage('ComposeEditorIsNullDuringProcessSPLinks', [linkIndex < 20 ? linkIndex : 20]);
            return;
        }

        // If linkId changes during making server call
        // user has probably changed the link. No need to continue
        if (!areIdsFromSameLink(linkId, anchorElement.id)) {
            trace.warn(
                `LinkId changes during server call LinkIdBefore: ${linkId}, LinkIdNow: ${
                    anchorElement.id
                }`
            );
            return;
        }

        // This is the scenario when user converts the anchor element to text, no need to continue
        const linksMatchingId = linkPlugin.editor.queryElements('#' + anchorElement.id);
        if (
            !linksMatchingId ||
            linksMatchingId.length !== 1 ||
            linksMatchingId[0] !== anchorElement
        ) {
            trace.warn(
                `The result from querying the editor is not matching the original anchor element with id: ${
                    anchorElement.id
                }`
            );

            return;
        }

        // Undo snapshot must be created before the id is prefixed with the special beautified token
        linkPlugin.editor.addUndoSnapshot();
        const isValidResponse: boolean =
            response && response.ResultCode === AttachmentResultCode.Success;
        const hasValidFileName: boolean = !isStringNullOrWhiteSpace(response.FileName);

        if (shouldBeautifyLink && isValidResponse && hasValidFileName) {
            addBeautifiedPrefixToId(anchorElement);
        }

        if (isValidResponse && !hasValidFileName) {
            logUsage('AttachmentComposeSPLinksResponseHasValidFileName');
        }
    } else if (shouldBeautifyLink) {
        trace.error(
            'We should not pass true for shouldBeautifyLink when processing the same link again.'
        );
    }

    // If sharingLink already exists, we don't need to add it to model store nor update the sharing tips
    // we just need to create ComposeLinkViewState.
    if (!sharingLink) {
        onSharePointLinkDetected(
            anchorElement.id,
            anchorElement.href,
            viewState.sharingLinkIds,
            response,
            shouldBeautifyLink || shouldRehydrate,
            isPreviouslyBeautified
        );
    } else if (isPreviouslyBeautified || sharingLink.sharingInfo.getSharingInfoSucceeded) {
        addComposeLinkViewState(anchorElement.id);
    }

    // Always try to check recipient permission
    const tryCheckPermForOneLink = await lazyTryCheckPermForOneLink.import();
    tryCheckPermForOneLink(anchorElement.id, linkPlugin.recipientWells, linkPlugin.fromAddress);

    // No other UI changes, just leave
    if (!shouldBeautifyLink && !shouldRehydrate) {
        return;
    }

    // Retrieve sharingLink if we haven't got one.
    if (!sharingLink) {
        sharingLink = getSharingLink(anchorElement.id);
    }

    const isLinkWithValidFileName: boolean =
        sharingLink && !isStringNullOrWhiteSpace(sharingLink.fileName);
    const isValidSharingInfo: boolean =
        sharingLink && sharingLink.sharingInfo.getSharingInfoSucceeded;

    // Beautify or rehydrate the link when conditions are met.
    if (isValidSharingInfo || isPreviouslyBeautified) {
        createSharingLinkView(
            linkPlugin.editor,
            linkPlugin.recipientWells,
            linkPlugin.fromAddress,
            viewState.composeId,
            anchorElement,
            sharingLink,
            isLinkWithValidFileName,
            sharingLink.mimeType,
            shouldBeautifyLink,
            isPreviouslyBeautified,
            shouldTryShowContextMenuOnMount
        );

        // call lazyDocLinkHadPreviewInfo and it will prefetch getWacInfo
        // so the context menu can determine to show preview menu item or not earlier
        if (isFeatureEnabled('doc-sharepoint-link-preview')) {
            const docLinkHadPreviewInfo = await lazyDocLinkHadPreviewInfo.import();
            docLinkHadPreviewInfo(anchorElement.href);
        }
    }
}

function displaySpinner(anchorElement: HTMLAnchorElement): Node[] {
    const newElements: Node[] = [];
    anchorElement.setAttribute('contenteditable', 'false');
    anchorElement.style.pointerEvents = 'none';
    anchorElement.addEventListener('click', (event: MouseEvent) => {
        event.preventDefault();
    });

    const spinnerContainer: HTMLElement = document.createElement('span');
    spinnerContainer.className = SPINNER_CLASSNAME;

    newElements.push(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_START));
    newElements.push(spinnerContainer);
    newElements.push(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_END));
    newElements.forEach(newElement => {
        anchorElement.appendChild(newElement);
    });

    displaySpinnerOverlay(spinnerContainer);
    return newElements;
}

function removeSpinner(anchorElement: HTMLAnchorElement, addedElements: Node[]) {
    addedElements.forEach(element => {
        try {
            anchorElement.removeChild(element);
        } catch (Exception) {
            // In case user has removed the spinner
        }
    });

    anchorElement.setAttribute('contenteditable', 'true');
    anchorElement.style.pointerEvents = 'auto';
    anchorElement.removeEventListener('click', (event: MouseEvent) => {
        event.preventDefault();
    });
}

function areIdsFromSameLink(oldId: string, newId: string): boolean {
    return oldId === newId;
}
